import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { get, set } from 'lodash/fp';
import { useCallback, useMemo, useState, VFC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import { callHelper, getHandlebars } from 'dashboard/lib/utils/handlebars';
import { deepTransformKeys, getJsonSchemaType, snakify } from 'dashboard/lib/workflows';
import {
  findMatchingObjectOrArrayPaths,
  flattenKeyValue,
  getArrayJsonSchemaType,
  Operator,
} from 'dashboard/lib/workflows/objects';
import { BaseTrigger } from 'dashboard/models/trigger';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Select, { Option as SelectOption } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaCustomTypes } from 'lib/fields';
import {
  JsonSchema,
  JsonSchemaArray,
  JsonSchemaBasicTypes,
  JsonSchemaObject,
  JsonSchemaTypes,
} from 'lib/json-schema';

interface KeyValue {
  [key: string]: string | KeyValue;
}

interface EditObjectKeyValuePairsPanelProps {
  object: string;
  objectFieldsJsonSchema: JsonSchemaObject;
  usableObjectsJsonSchema: JsonSchema;
  keyValueObject: KeyValue;
  keyOperatorObject: KeyValue;
  stepTokensToDisplayValue: Record<string, string>;
  updateOperatorsAndObjects: (operatorsAndValues: {
    keyOperatorObject: Record<string, string>;
    keyValueObject: Record<string, string>;
  }) => void;
  syntheticAccountsJsonSchema: JsonSchemaObject;
}

const FieldValueMinWidthContainer = styled.div`
  min-width: 50%;
`;

const GridHeader = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 128px 1fr 32px;
  column-gap: ${Spacing[1]};
  margin-bottom: ${Spacing[3]};
`;

const Grid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 128px 1fr 32px;
  column-gap: ${Spacing[1]};
  margin-bottom: ${Spacing[4]};
`;

const MinWidthContainer = styled.div`
  min-width: 32px;
`;

interface UpdateKeyValue {
  field: string;
  value?: string;
  operator?: Operator;
}

const toObject = (keyValueObjectArray: UpdateKeyValue[]) =>
  keyValueObjectArray
    .filter(({ field, value }) => field !== '' && value != null && value.length > 0)
    .reduce((acc, { field, value }) => set(field.split('.'), value, acc), {});

const toOperators = (keyValueObjectArray: UpdateKeyValue[]) =>
  keyValueObjectArray
    .filter(
      ({ field, value, operator }) =>
        field !== '' && ((value != null && value.length > 0) || operator === Operator.Unset),
    )
    .reduce((acc, { field, operator }) => {
      // in an ideal world, we would backfill all existing wfv to back fill the operator.
      // however, since that is a lot of work/time consuming, we are just going to
      // fill in the operator as `Set` if it doesn't already exist.
      // this way, wfv can get updated as folks
      // 1. get opted into the feature flag `dashboard:show-revamped-update-object-fields`
      // 2. has made an edit with that feature flag enabled
      const effectiveOperator = operator ?? Operator.Set;
      return set(field.split('.'), effectiveOperator, acc);
    }, {});

const EMPTY_KEY_VALUE_OPERATOR: UpdateKeyValue = {
  field: '',
  value: undefined,
  operator: Operator.Set,
};

const DEFAULT_OPERATORS = [
  {
    label: 'Set',
    value: Operator.Set,
  },
  {
    label: 'Unset',
    value: Operator.Unset,
  },
];

const getDisallowedFieldPaths = (
  objectDisplayName: string | undefined,
  field: string,
  syntheticAccountsJsonSchema: JsonSchemaObject,
) => {
  // we want to avoid situations where we assign the left side field to itself since it is confusing
  // since the source field schema is prefixed by the display step token we need to use that when disallowing fields
  if (objectDisplayName == null) {
    return [];
  }
  const normalizedField = snakify(field);
  // there are two cases here
  // 1. `field` is an actual field on the object. in which case we want to disallowe fields.`${field}`
  // 2. `field` is a legacy account property that is not actually a real field. if that is the case then we
  // want to disallow it directly from the object
  if (syntheticAccountsJsonSchema.properties?.[normalizedField] == null) {
    return [`${objectDisplayName}.fields.${normalizedField}`];
  } else {
    return [`${objectDisplayName}.${normalizedField}`];
  }
};

const HANDLEBARS_SUPPORTED_TYPES: (JsonSchemaTypes | JsonSchemaCustomTypes)[] = [
  JsonSchemaTypes.String,
  JsonSchemaTypes.Number,
  JsonSchemaTypes.Boolean,
  JsonSchemaTypes.Integer,
  JsonSchemaCustomTypes.Date,
  JsonSchemaCustomTypes.Enum,
  JsonSchemaCustomTypes.MultiEnum,
  JsonSchemaCustomTypes.GroupedEnum,
];

const getInitialKeyValueObjectArray = (keyValueObject: KeyValue, keyOperatorObject: KeyValue) => {
  if (Object.keys(keyValueObject).length === 0 && Object.keys(keyOperatorObject).length === 0) {
    return [EMPTY_KEY_VALUE_OPERATOR];
  }

  const keyValueObjectEntries: {
    field: string;
    value?: string;
    operator: Operator;
  }[] = flattenKeyValue(keyValueObject).map(([key, value]) => ({
    field: key,
    value: value as string,
    operator: get(key.split('.'), keyOperatorObject) as Operator,
  }));
  const existingKeys = new Set(keyValueObjectEntries.map((field) => field.field));
  const items = flattenKeyValue<Operator>(keyOperatorObject)
    .filter(([key, operator]) => !existingKeys.has(key) && operator === Operator.Unset)
    .map(([key, operator]) => ({
      field: key,
      value: undefined,
      operator: operator,
    }));
  return keyValueObjectEntries
    .concat(items)
    .sort((left, right) => left.field.localeCompare(right.field));
};

const EditObjectKeyValuePairsPanel: VFC<EditObjectKeyValuePairsPanelProps> = ({
  object,
  objectFieldsJsonSchema,
  usableObjectsJsonSchema,
  keyValueObject,
  keyOperatorObject,
  stepTokensToDisplayValue,
  updateOperatorsAndObjects,
  syntheticAccountsJsonSchema,
}) => {
  const schemaForFieldPath = useSchemaForFieldPath();

  const [keyValueObjectArray, setKeyValueObjectArray] = useState<UpdateKeyValue[]>(
    getInitialKeyValueObjectArray(keyValueObject, keyOperatorObject),
  );
  const transformedInflectionJsonSchema = useMemo(
    () => ({
      ...objectFieldsJsonSchema,
      properties: deepTransformKeys(objectFieldsJsonSchema.properties, (key) =>
        key.replace(/_/g, '-'),
      ),
    }),
    [objectFieldsJsonSchema],
  );

  const getAllowedTypesForSchema = (
    schema: JsonSchema | undefined,
  ): {
    allowedTypes?: (JsonSchemaTypes | JsonSchemaCustomTypes)[];
    allowedArrayTypes?: (JsonSchemaBasicTypes | JsonSchemaTypes.Object)[];
    schema?: JsonSchema;
  } => {
    const schemaType = schema == null ? null : getJsonSchemaType(schema);
    switch (schemaType) {
      case JsonSchemaTypes.Object: {
        return {
          allowedTypes: Object.values(JsonSchemaTypes),
          allowedArrayTypes: [
            JsonSchemaTypes.Boolean,
            JsonSchemaTypes.Integer,
            JsonSchemaTypes.Number,
            JsonSchemaTypes.String,
            JsonSchemaTypes.Object,
          ],
          schema,
        };
      }
      case JsonSchemaTypes.Array: {
        const allowedArrayType = getArrayJsonSchemaType(schema as JsonSchemaArray);
        return {
          schema,
          allowedTypes: [JsonSchemaTypes.Array, JsonSchemaTypes.Object],
          allowedArrayTypes: allowedArrayType == null ? [] : [allowedArrayType],
        };
      }
      default:
        return {
          allowedTypes: schemaType == null ? undefined : [schemaType],
          allowedArrayTypes: undefined,
          schema,
        };
    }
  };

  const getAllowedTypesForField = useCallback(
    (
      currentKey: string,
      operator?: Operator,
    ): {
      allowedTypes?: (JsonSchemaTypes | JsonSchemaCustomTypes)[];
      allowedArrayTypes?: (JsonSchemaBasicTypes | JsonSchemaTypes.Object)[];
      schema?: JsonSchema;
    } => {
      const { schema } = schemaForFieldPath(currentKey, transformedInflectionJsonSchema);
      // if currentKey is an array, and we are pushing, then we want to look for fields that are compatible
      // with schema.items
      if (
        operator &&
        [Operator.Push, Operator.Remove].includes(operator) &&
        schema != null &&
        'items' in schema
      ) {
        return getAllowedTypesForSchema(schema.items);
      }
      return getAllowedTypesForSchema(schema || undefined);
    },
    [transformedInflectionJsonSchema],
  );

  // TODO: simplify data flows for KeyValueObjectArray
  // See comment here: https://github.com/persona-id/persona-web/issues/40297#issuecomment-2157305515
  const updateKey = useCallback(
    (idx: number, value: string) => {
      if (keyValueObjectArray[idx].field === value) {
        return;
      }
      const newKV = [...keyValueObjectArray];
      newKV[idx] = {
        ...EMPTY_KEY_VALUE_OPERATOR,
        field: value,
      };
      setKeyValueObjectArray(newKV);
      updateOperatorsAndObjects({
        keyOperatorObject: toOperators(newKV),
        keyValueObject: toObject(newKV),
      });
    },
    [keyValueObjectArray, updateOperatorsAndObjects],
  );

  const updateValue = useCallback(
    (idx: number, value: string) => {
      const newKV = [...keyValueObjectArray];
      const handlebarsVariables = getHandlebars(value);
      // the backend supports either
      // a) a constant (string) value or
      // b) a single method chain.
      // to make that clear, we throw away any existing constant value when we add a variable.
      // if we add another handlebars variable, then we throw away the existing one.
      const updatedValue =
        handlebarsVariables.length > 0
          ? `{{${callHelper(handlebarsVariables[handlebarsVariables.length - 1])}}}`
          : value;
      newKV[idx] = {
        ...newKV[idx],
        value: updatedValue,
      };
      setKeyValueObjectArray(newKV);
      updateOperatorsAndObjects({
        keyOperatorObject: toOperators(newKV),
        keyValueObject: toObject(newKV),
      });
    },
    [keyValueObjectArray, updateOperatorsAndObjects],
  );

  const addNewKeyValue = useCallback(() => {
    setKeyValueObjectArray([...keyValueObjectArray, EMPTY_KEY_VALUE_OPERATOR]);
    updateOperatorsAndObjects({
      keyOperatorObject: toOperators(keyValueObjectArray),
      keyValueObject: toObject(keyValueObjectArray),
    });
  }, [keyValueObjectArray, updateOperatorsAndObjects]);

  const removeKeyValue = useCallback(
    (idx: number) => {
      const newKV = [...keyValueObjectArray];
      newKV.splice(idx, 1);
      setKeyValueObjectArray(newKV);
      updateOperatorsAndObjects({
        keyOperatorObject: toOperators(newKV),
        keyValueObject: toObject(newKV),
      });
    },
    [keyValueObjectArray, updateOperatorsAndObjects],
  );

  const updateOperator = useCallback(
    (idx: number, option: Operator) => {
      const newKOA = [...keyValueObjectArray];
      const oldOperator = newKOA[idx].operator ?? Operator.Set;
      const newOperator = option as Operator;
      if (
        oldOperator !== newOperator &&
        (oldOperator === Operator.Push ||
          newOperator === Operator.Push ||
          newOperator === Operator.Unset)
      ) {
        // if we go to/from a Push, we know that the two types are not compatible.
        // so we clear out the value to save the user some work.
        // alternatively, if we are going to an Unset, we don't use the value and we should zero out the value
        newKOA[idx] = { ...newKOA[idx], operator: option as Operator, value: undefined };
      } else {
        newKOA[idx] = { ...newKOA[idx], operator: option as Operator };
      }

      setKeyValueObjectArray(newKOA);
      updateOperatorsAndObjects({
        keyOperatorObject: toOperators(newKOA),
        keyValueObject: toObject(newKOA),
      });
    },
    [keyValueObjectArray, updateOperatorsAndObjects],
  );

  const getOperatorsFor = useCallback(
    (property: string) => {
      const { schema: returnSchemaForFieldPath } = schemaForFieldPath(
        property,
        transformedInflectionJsonSchema,
      );
      if (returnSchemaForFieldPath == null) {
        return [...DEFAULT_OPERATORS];
      }
      const jsonSchemaType = getJsonSchemaType(returnSchemaForFieldPath);
      if (jsonSchemaType === JsonSchemaTypes.Array) {
        return [
          {
            label: 'Set',
            value: Operator.Set,
          },
          {
            label: 'Unset',
            value: Operator.Unset,
          },
          {
            label: 'Append',
            value: Operator.Append,
          },
          {
            label: 'Push',
            value: Operator.Push,
          },
          {
            label: 'Remove',
            value: Operator.Remove,
          },
        ];
      }

      return DEFAULT_OPERATORS;
    },
    [transformedInflectionJsonSchema],
  );
  return (
    <>
      <GridHeader>
        <div>Field</div>
        <div>Operator</div>
        <div>Value</div>
      </GridHeader>
      {keyValueObjectArray.map(({ field, operator, value }: UpdateKeyValue, idx) => {
        const { allowedTypes, allowedArrayTypes, schema } = getAllowedTypesForField(
          field,
          operator,
        );
        const jsonSchemaType = schema != null ? getJsonSchemaType(schema) : null;
        // allowedFieldPaths is only set for array and object types. by default, JsonSchemaSelect provides enough
        // type matching for primitives to give us what we need. however, for array item type and object type matching
        // we have very be-spoke matching criteria, so we just find the paths we want and pass them into
        // JsonSchemaSelect
        const allowedFieldPaths =
          jsonSchemaType === JsonSchemaTypes.Object || jsonSchemaType === JsonSchemaTypes.Array
            ? findMatchingObjectOrArrayPaths(schema!, usableObjectsJsonSchema)
            : undefined;
        const fullyQualifiedDisallowedFieldPaths = getDisallowedFieldPaths(
          object === BaseTrigger ? object : stepTokensToDisplayValue[object],
          field,
          syntheticAccountsJsonSchema,
        );

        const handlebarsVariables = value == null ? [] : getHandlebars(value);
        return (
          <Grid
            aria-label={`row ${idx} with property ${field || 'not yet selected'}`}
            key={`property-${field}-grid-${idx}`}
          >
            <MinWidthContainer>
              <JsonSchemaSelect
                ariaLabel={`target property name ${idx}`}
                id={`edit-object-key-value-pairs-panel__key-input-idx-${idx}`}
                inputValue={field}
                jsonSchema={transformedInflectionJsonSchema}
                allowedTypes={undefined}
                disabled={false}
                allowedArrayTypes={[
                  JsonSchemaTypes.Boolean,
                  JsonSchemaTypes.String,
                  JsonSchemaTypes.Number,
                  JsonSchemaTypes.Integer,
                  JsonSchemaTypes.Object,
                ]}
                canSelectObjects={true}
                disallowedFieldPaths={keyValueObjectArray
                  .filter((currentKey) => currentKey.field !== field)
                  .map((entry) => entry.field)}
                tokensToDisplayValue={stepTokensToDisplayValue}
                onFieldSelect={() => undefined}
                onValueChange={(value) => updateKey(idx, value as string)}
              />
            </MinWidthContainer>
            <Select
              value={operator}
              onValueChange={(value: Operator) => updateOperator(idx, value)}
              disabled={false}
              placeholder='Select operator'
              id={`edit-object-key-value-pairs-panel__operator-input-${idx}`}
            >
              {getOperatorsFor(field).map((operator) => (
                <SelectOption key={operator.value} value={operator.value}>
                  {operator.label}
                </SelectOption>
              ))}
            </Select>
            <FieldValueMinWidthContainer>
              {operator === Operator.Unset ||
              jsonSchemaType == null ||
              HANDLEBARS_SUPPORTED_TYPES.includes(jsonSchemaType) ? (
                <HandlebarFormControl
                  aria-label={`source property select ${idx}`}
                  size='md'
                  type='input'
                  key={`header-value-${idx}`}
                  value={value ?? ''}
                  updateValue={(value: string) => updateValue(idx, value)}
                  editable={operator !== Operator.Unset}
                  pillsEditable={operator !== Operator.Unset}
                  addPillButtonDisabled={operator === Operator.Unset}
                  allowedTypes={allowedTypes}
                  allowedArrayTypes={allowedArrayTypes}
                  jsonSchema={usableObjectsJsonSchema}
                  tokensToDisplayValue={stepTokensToDisplayValue}
                  canSelectObjects={true}
                  allowedFieldPaths={allowedFieldPaths}
                  fieldPathsAreStrictMatch={true}
                  disallowedFieldPaths={fullyQualifiedDisallowedFieldPaths}
                  placeholder={operator === Operator.Unset ? '' : 'Property Value'}
                  id={`edit-object-key-value-pairs-panel__value-input-${idx}`}
                  style={{ maxHeight: '100px', overflow: 'hidden' }}
                />
              ) : (
                <JsonSchemaSelect
                  ariaLabel={`source property schema select ${idx}`}
                  id={`edit-object-key-value-pairs-panel__key-input-idx-${idx}`}
                  inputValue={value == null ? '' : handlebarsVariables[0]}
                  jsonSchema={usableObjectsJsonSchema}
                  allowedTypes={allowedTypes}
                  allowedArrayTypes={allowedArrayTypes}
                  disabled={false}
                  canSelectObjects={true}
                  allowedFieldPaths={allowedFieldPaths}
                  fieldPathsAreStrictMatch={true}
                  tokensToDisplayValue={stepTokensToDisplayValue}
                  disallowedFieldPaths={fullyQualifiedDisallowedFieldPaths}
                  onFieldSelect={(data: Option | Option[]) => {
                    if (Array.isArray(data)) {
                      updateValue(idx, `{{${callHelper(data[0].value)}}}`);
                    } else {
                      updateValue(idx, `{{${callHelper(data.value)}}}`);
                    }
                  }}
                  onValueChange={(value) => updateValue(idx, value as string)}
                />
              )}
            </FieldValueMinWidthContainer>
            <Button
              id={`edit-object-key-value-pairs-panel__input-remove-${idx}`}
              variant='basic'
              disabled={false}
              width='icon'
              aria-label={`delete property ${idx}`}
              onClick={() => removeKeyValue(idx)}
            >
              <TrashIcon />
            </Button>
          </Grid>
        );
      })}
      <Spacer height={2} />
      <Button
        variant='basic'
        width='auto'
        disabled={false}
        onClick={addNewKeyValue}
        id='edit-object-key-value-pairs-panel__add-button'
        prefixIcon={<PlusIcon />}
      >
        Add Field
      </Button>
    </>
  );
};

export default EditObjectKeyValuePairsPanel;
